import React from "react";
import Checkbox from "@mui/material/Checkbox";
import StoreOutlinedIcon from "@mui/icons-material/StoreOutlined";

// The task form's location picker: regions > districts > stores from the
// company's store groups payload. Ticking a region or district picks the
// GROUP (its stores show as checked and inherited); ticking a store on its own
// is an explicit pick. Managers (allowGroups=false) see the tree as headers
// and may tick only the stores they manage. With no groups at all this is the
// plain flat list the form always had.

const checkboxSx = { color: "#cbd5e1", "&.Mui-checked": { color: "#6d28d9" }, "&.Mui-disabled": { color: "#c4b5fd" }, py: 0.5 };
const byName = (a, b) => (a.name || a.store_name || "").localeCompare(b.name || b.store_name || "");

export function scopeReach(groups, stores, groupUuids, storeIds, excludedIds = []) {
  // The stores a set of picks expands to, mirroring the backend: a region
  // reaches its own stores and its districts'; a district its own.
  const districtsOf = (regionUuid) => (groups || []).filter((g) => g.kind === "district" && g.parent_uuid === regionUuid);
  const reached = new Set(storeIds || []);
  // A store flagged inherited by the caller (a closed store a pick reaches,
  // absent from the open-stores payload) counts whatever its group.
  (stores || []).forEach((s) => { if (s.inherited) reached.add(s.id); });
  (groupUuids || []).forEach((uuid) => {
    const group = (groups || []).find((g) => g.uuid === uuid);
    if (!group) return;
    const covered = [uuid, ...(group.kind === "region" ? districtsOf(uuid).map((d) => d.uuid) : [])];
    (stores || []).forEach((s) => { if (covered.includes(s.store_group_uuid)) reached.add(s.id); });
  });
  // A member a one-off leaves out (it would open there after its deadline).
  (excludedIds || []).forEach((id) => reached.delete(id));
  return reached;
}

export default function StoreScopeTree({
  groups = [],
  stores = [],
  selectedGroupUuids = [],
  selectedStoreIds = [],
  excludedStoreIds = [],
  onChange,
  allowGroups = true,
  selectableStoreIds = null,
}) {
  const sortedGroups = React.useMemo(() => [...groups].sort(byName), [groups]);
  const sortedStores = React.useMemo(() => [...stores].sort(byName), [stores]);
  const regions = sortedGroups.filter((g) => g.kind === "region");
  const districts = sortedGroups.filter((g) => g.kind === "district");
  const districtsOf = (regionUuid) => districts.filter((d) => d.parent_uuid === regionUuid);
  const storesOf = (groupUuid) => sortedStores.filter((s) => s.store_group_uuid === groupUuid);
  // A store whose group uuid names no group in the payload (a closed store a
  // pick reached; its group's section still exists) is listed as ungrouped.
  const knownGroupUuids = new Set(sortedGroups.map((g) => g.uuid));
  const unassigned = sortedStores.filter((s) => !s.store_group_uuid || !knownGroupUuids.has(s.store_group_uuid));
  const groupByUuid = (uuid) => sortedGroups.find((g) => g.uuid === uuid);
  const nameOf = (uuid) => groupByUuid(uuid)?.name;

  const groupPicked = (uuid) => selectedGroupUuids.includes(uuid);
  // The group a store inherits its tick from, if any: its own district when
  // picked, else the region above that district when picked. A store picked
  // on its own is explicit whatever group it sits in (a resync keeps the
  // explicit row when a store moves into a picked group), so it stays
  // tickable here and is saved as what it is.
  const isExcluded = (store) => excludedStoreIds.includes(store.id);
  const inheritedFrom = (store) => {
    if (selectedStoreIds.includes(store.id)) return null;
    if (isExcluded(store)) return null;
    if (store.inherited) return "__inherited__";
    if (!store.store_group_uuid) return null;
    if (groupPicked(store.store_group_uuid)) return store.store_group_uuid;
    const parent = groupByUuid(store.store_group_uuid)?.parent_uuid;
    return parent && groupPicked(parent) ? parent : null;
  };
  const districtInheritedFrom = (district) => (district.parent_uuid && groupPicked(district.parent_uuid) ? district.parent_uuid : null);
  const storeSelectable = (store) => selectableStoreIds == null || selectableStoreIds.includes(store.id);

  const emit = (groupUuids, storeIds) => onChange?.({ groupUuids, storeIds });
  const toggleGroup = (uuid) => {
    if (!allowGroups) return;
    if (groupPicked(uuid)) {
      // Unpicking a group touches nothing else: an explicit pick stays one.
      emit(selectedGroupUuids.filter((u) => u !== uuid), selectedStoreIds);
      return;
    }
    let next = [...selectedGroupUuids, uuid];
    // What the group just picked reaches is inherited, not explicit: drop
    // the standalone picks of its districts (a region) and of the stores it
    // covers, so what the picker shows is what gets saved (an explicit pick
    // would otherwise outlive the district or store leaving the group). Only
    // THIS group's reach: an explicit pick sitting inside a group picked
    // earlier is deliberate (see inheritedFrom) and is left alone.
    const picked = groupByUuid(uuid);
    if (picked?.kind === "region") {
      const childUuids = districtsOf(uuid).map((d) => d.uuid);
      next = next.filter((u) => !childUuids.includes(u));
    }
    const covered = scopeReach(sortedGroups, sortedStores, [uuid], []);
    emit(next, selectedStoreIds.filter((id) => !covered.has(id)));
  };
  const toggleStore = (storeId) => {
    const next = selectedStoreIds.includes(storeId) ? selectedStoreIds.filter((id) => id !== storeId) : [...selectedStoreIds, storeId];
    emit(selectedGroupUuids, next);
  };

  const reach = scopeReach(sortedGroups, sortedStores, selectedGroupUuids, selectedStoreIds, excludedStoreIds);
  const pickedNames = selectedGroupUuids.map(nameOf).filter(Boolean);
  const explicitCount = selectedStoreIds.filter((id) => {
    const store = sortedStores.find((s) => s.id === id);
    return !store || !inheritedFrom(store);
  }).length;
  const reachParts = [];
  if (pickedNames.length) reachParts.push(pickedNames.join(", "));
  if (explicitCount) reachParts.push(`${explicitCount} ${explicitCount === 1 ? "location" : "locations"} on their own`);

  const storeRow = (store, indent) => {
    const via = inheritedFrom(store);
    const excluded = isExcluded(store) && !selectedStoreIds.includes(store.id);
    const checked = Boolean(via) || selectedStoreIds.includes(store.id);
    const disabled = Boolean(via) || excluded || !storeSelectable(store);
    return (
      <label key={store.id} className={`flex min-h-[38px] items-center gap-2 py-1 pr-3 ${disabled ? "" : "cursor-pointer hover:bg-slate-50"}`} style={{ paddingLeft: indent }}>
        <Checkbox checked={checked} disabled={disabled} onChange={() => toggleStore(store.id)} size="small" inputProps={{ "aria-label": store.store_name }} sx={checkboxSx} />
        <StoreOutlinedIcon style={{ fontSize: 14 }} className="text-slate-400" />
        <span className={`flex-1 text-sm ${checked ? "font-medium text-violet-800" : storeSelectable(store) ? "text-slate-700" : "text-slate-400"}`}>{store.store_name}</span>
        {via && <span className="text-[11px] text-slate-400">{via === "__inherited__" ? "via a picked group" : `via ${nameOf(via)}`}</span>}
        {excluded && <span className="text-[11px] text-amber-700">not a target: opens after it is due</span>}
        {!via && checked && store.store_group_uuid && (groupPicked(store.store_group_uuid) || groupPicked(groupByUuid(store.store_group_uuid)?.parent_uuid)) && (
          <span className="text-[11px] text-slate-400">on its own</span>
        )}
      </label>
    );
  };

  const groupRow = (group, indent) => {
    const via = group.kind === "district" ? districtInheritedFrom(group) : null;
    const checked = groupPicked(group.uuid) || Boolean(via);
    const count = group.kind === "region"
      ? storesOf(group.uuid).length + districtsOf(group.uuid).reduce((n, d) => n + storesOf(d.uuid).length, 0)
      : storesOf(group.uuid).length;
    if (!allowGroups) {
      return (
        <div key={group.uuid} className="flex min-h-[36px] items-center gap-2 py-1 pr-3" style={{ paddingLeft: indent }}>
          <span className="text-sm font-semibold text-slate-900">{group.name}</span>
          {group.kind === "region" && <span className="rounded bg-violet-100 px-1.5 py-px text-[10px] font-semibold uppercase tracking-wider text-violet-700">Region</span>}
          <span className="text-xs text-slate-500">{count} locations</span>
        </div>
      );
    }
    return (
      <label key={group.uuid} className={`flex min-h-[40px] items-center gap-2 py-1 pr-3 ${via ? "" : "cursor-pointer hover:bg-slate-50"}`} style={{ paddingLeft: indent }}>
        <Checkbox checked={checked} disabled={Boolean(via)} onChange={() => toggleGroup(group.uuid)} size="small" inputProps={{ "aria-label": `${group.name} ${group.kind}` }} sx={checkboxSx} />
        <span className={`text-sm font-semibold ${checked ? "text-violet-800" : "text-slate-900"}`}>{group.name}</span>
        {group.kind === "region" && <span className="rounded bg-violet-100 px-1.5 py-px text-[10px] font-semibold uppercase tracking-wider text-violet-700">Region</span>}
        <span className="text-xs text-slate-500">{count} locations</span>
        {via && <span className="ml-auto text-[11px] text-slate-400">via {nameOf(via)}</span>}
      </label>
    );
  };

  const topLevelDistricts = districts.filter((d) => !d.parent_uuid);
  const flat = sortedGroups.length === 0;

  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between">
        <span className="text-xs font-semibold text-slate-600">{reach.size} {reach.size === 1 ? "location" : "locations"}</span>
        {reachParts.length > 0 && <span className="truncate pl-3 text-xs text-slate-500">{reachParts.join(" + ")}</span>}
      </div>
      <div className="max-h-[360px] overflow-y-auto rounded-lg border border-slate-200 bg-white">
        {sortedStores.length === 0 && <div className="px-3 py-6 text-center text-sm text-slate-500">No locations in this company.</div>}
        {flat && sortedStores.map((store) => storeRow(store, 12))}
        {!flat && regions.map((region) => (
          <div key={region.uuid} className="border-b border-slate-100 last:border-b-0">
            {groupRow(region, 12)}
            {districtsOf(region.uuid).map((district) => (
              <div key={district.uuid}>
                {groupRow(district, 40)}
                {storesOf(district.uuid).map((store) => storeRow(store, 68))}
              </div>
            ))}
            {storesOf(region.uuid).map((store) => storeRow(store, 40))}
          </div>
        ))}
        {!flat && topLevelDistricts.map((district) => (
          <div key={district.uuid} className="border-b border-slate-100 last:border-b-0">
            {groupRow(district, 12)}
            {storesOf(district.uuid).map((store) => storeRow(store, 40))}
          </div>
        ))}
        {!flat && unassigned.length > 0 && (
          <div>
            <div className="flex min-h-[36px] items-center gap-2 py-1 pl-3 pr-3">
              <span className="text-sm font-semibold text-slate-900">Not in a group</span>
              <span className="text-xs text-slate-500">{unassigned.length} locations</span>
            </div>
            {unassigned.map((store) => storeRow(store, 40))}
          </div>
        )}
      </div>
    </div>
  );
}
